import React, { useEffect, useState } from 'react';
import { useLocalization, useTranslate } from '@im/react-localization';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { usePNAforWarehouse } from '../use-pna-warehouse';
import {
  EMEA_COUNTRY_CODES,
  WarehouseDetails,
  Warehouse,
} from './common.types';
import { TableSkeleton } from '@im/ui-lib/TableSkeleton';
import { useDecodedToken } from 'mf_core/auth';
import WarehouseEMEAGridData from './WarehouseEMEAGridData';

type WarehouseEMEADetailsProps = {
  ShowCentralStockTtypeSku: boolean;
  fromPage: string;
  id: any;
  licenseSelectorVendorSubrange?: string;
  onClose: () => void;
  open: boolean;
  productType?: string;
  reserveFlag: boolean;
  sku: string;
  skuType?: string;
  vendorCode?: string;
  warehouseDetails: WarehouseDetails[];
  warehouses?: Warehouse[];
};

export function WarehouseEMEADetailsModal(props: WarehouseEMEADetailsProps) {
  const {
    ShowCentralStockTtypeSku,
    fromPage,
    licenseSelectorVendorSubrange,
    onClose,
    open,
    productType,
    sku,
    skuType,
    vendorCode,
    warehouseDetails,
  } = props;
  const translate = useTranslate();
  const { formatDate } = useLocalization();
  const decodedToken = useDecodedToken();
  const siteCode = decodedToken?.siteCode;
  const [warehouseStockDetailUpdate, setwarehouseStockDetailUpdate] = useState<
    WarehouseDetails[]
  >([]);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const { data, isLoading } = usePNAforWarehouse(
    sku,
    skuType,
    vendorCode,
    licenseSelectorVendorSubrange
  );
  const warehouseProductDetails = data?.[0].warehouseList;

  const sortFunction = (a: any, b: any) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA > dateB ? 1 : -1;
  };

  function isEligibleForProductTypeNullEmpty(productType: any) {
    return productType === null || productType === '';
  }

  function isEligibleForProductTypTW(productType: any) {
    return productType === 'T' || productType === 'W';
  }

  useEffect(() => {
    if (isInitialRender && data !== undefined) {
      setIsInitialRender(false);
      if (warehouseProductDetails) {
        //SkuTypeNull
        const warehouseStockListProductTypeNull =
          isEligibleForProductTypeNullEmpty(productType) &&
          ShowCentralStockTtypeSku &&
          warehouseProductDetails
            .filter((x: any) => x.warehouseLocation === 'Local')
            .map((warehouse: any) => {
              return {
                estimatedLeadTime: warehouse.leadTimeEta,
                id: warehouse.warehousseId,
                name: warehouse.warehouseLocation,
                onOrderQuantity: warehouse.onOrderQuantity || 0,
                productType: productType,
                quantityAvailable: warehouse.warehouseAvailableQuantity || 0,
                quantityOnOrder:
                  EMEA_COUNTRY_CODES.includes(siteCode?.toUpperCase()) &&
                  warehouse.quantityOnOrder !== null &&
                  warehouse.quantityOnOrder !== undefined &&
                  warehouse.quantityOnOrder
                    ?.slice()
                    .sort((a: any, b: any) => sortFunction(a, b))
                    .map((qtyOrderList: any, i: number) => (
                      <>
                        <tr key={i}>
                          <td>{qtyOrderList.quantity || 0}</td>{' '}
                        </tr>
                      </>
                    )),
                quantityOnOrderEta:
                  EMEA_COUNTRY_CODES.includes(siteCode?.toUpperCase()) &&
                  warehouse.quantityOnOrder !== null &&
                  warehouse.quantityOnOrder !== undefined &&
                  warehouse.quantityOnOrder
                    ?.slice()
                    .sort((a: any, b: any) => sortFunction(a, b))
                    .map((qtyOrderList: any, i: number) => (
                      <>
                        <tr key={i}>
                          <td>{formatDate(qtyOrderList.date) || '-'}</td>
                        </tr>
                      </>
                    )),
              } as unknown as WarehouseDetails;
            });

        //SkuType T,W
        const warehouseStockList_T_W_ProductType =
          isEligibleForProductTypTW(productType) &&
          ShowCentralStockTtypeSku &&
          warehouseProductDetails
            .filter((y: any) => y.warehouseLocation === 'Central')
            .map((warehouseCentral: any) => {
              return {
                estimatedLeadTime: warehouseCentral.leadTimeEta,
                id: warehouseCentral.warehousseId,
                name: warehouseCentral.warehouseLocation,
                onOrderQuantity: warehouseCentral.onOrderQuantity || 0,
                productType: productType,
                quantityAvailable:
                  warehouseCentral.warehouseAvailableQuantity || 0,
                quantityOnOrder:
                  EMEA_COUNTRY_CODES.includes(siteCode?.toUpperCase()) &&
                  warehouseCentral.quantityOnOrder !== null &&
                  warehouseCentral.quantityOnOrder !== undefined &&
                  warehouseCentral.quantityOnOrder
                    ?.slice()
                    .sort((a: any, b: any) => sortFunction(a, b))
                    .map((qtyOrderList: any, i: number) => (
                      <>
                        <tr key={i}>
                          <td>{qtyOrderList.quantity || 0}</td>{' '}
                        </tr>
                      </>
                    )),
                quantityOnOrderEta:
                  EMEA_COUNTRY_CODES.includes(siteCode?.toUpperCase()) &&
                  warehouseCentral.quantityOnOrder !== null &&
                  warehouseCentral.quantityOnOrder !== undefined &&
                  warehouseCentral.quantityOnOrder
                    ?.slice()
                    .sort((a: any, b: any) => sortFunction(a, b))
                    .map((qtyOrderList: any, i: number) => (
                      <>
                        <tr key={i}>
                          <td>{formatDate(qtyOrderList.date) || '-'}</td>
                        </tr>
                      </>
                    )),
              } as WarehouseDetails;
            });

        //SkuType Other than T,W
        const warehouseStockListProductTypeNotNull =
          // isEligibleForProductTypeNotTW(productType) &&
          !ShowCentralStockTtypeSku &&
          warehouseProductDetails.map((warehouseOtherThanTW: any) => {
            return {
              estimatedLeadTime: warehouseOtherThanTW.leadTimeEta,
              id: warehouseOtherThanTW.warehousseId,
              name: warehouseOtherThanTW.warehouseLocation,
              onOrderQuantity: warehouseOtherThanTW.onOrderQuantity || 0,
              productType: productType,
              quantityAvailable:
                warehouseOtherThanTW.warehouseAvailableQuantity || 0,
              quantityOnOrder:
                EMEA_COUNTRY_CODES.includes(siteCode?.toUpperCase()) &&
                warehouseOtherThanTW.quantityOnOrder !== null &&
                warehouseOtherThanTW.quantityOnOrder !== undefined &&
                warehouseOtherThanTW.quantityOnOrder
                  ?.slice()
                  .sort((a: any, b: any) => sortFunction(a, b))
                  .map((qtyOrderList: any, i: number) => (
                    <>
                      <tr key={i}>
                        <td>{qtyOrderList.quantity || 0}</td>{' '}
                      </tr>
                    </>
                  )),
              quantityOnOrderEta:
                EMEA_COUNTRY_CODES.includes(siteCode?.toUpperCase()) &&
                warehouseOtherThanTW.quantityOnOrder !== null &&
                warehouseOtherThanTW.quantityOnOrder !== undefined &&
                warehouseOtherThanTW.quantityOnOrder
                  ?.slice()
                  .sort((a: any, b: any) => sortFunction(a, b))
                  .map((qtyOrderList: any, i: number) => (
                    <>
                      <tr key={i}>
                        <td>{formatDate(qtyOrderList.date) || '-'}</td>
                      </tr>
                    </>
                  )),
            } as WarehouseDetails;
          });

        if (warehouseStockListProductTypeNull) {
          setwarehouseStockDetailUpdate(warehouseStockListProductTypeNull);
        } else if (warehouseStockList_T_W_ProductType) {
          setwarehouseStockDetailUpdate(warehouseStockList_T_W_ProductType);
        } else {
          warehouseStockListProductTypeNotNull &&
            setwarehouseStockDetailUpdate(warehouseStockListProductTypeNotNull);
        }
      }
    }
  }, [
    data,
    formatDate,
    fromPage,
    isInitialRender,
    productType,
    ShowCentralStockTtypeSku,
    siteCode,
    warehouseDetails,
    warehouseProductDetails,
    warehouseStockDetailUpdate,
  ]);

  return (
    <>
      <Modal onClose={onClose} open={open}>
        {isLoading ? (
          <RootBox>
            <TableSkeleton
              columns={[
                '----------------------------------------------------------------------------',
                '--------------------',
                '-------------------------------------------------',
                '--------------------',
                '------------------------------------',
                '----------------------------------------------------------',
              ]}
              key={sku}
              rows={4}
            />
          </RootBox>
        ) : (
          <RootBox>
            <Typography
              sx={{
                display: 'flex',
                fontSize: '19px',
                justifyContent: 'space-between',
              }}
              variant="h5"
            >
              {translate('Warehouse.ETAPopupName')}
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>{' '}
            </Typography>
            <WarehouseEMEAGridData
              ShowCentralStockTtypeSku={ShowCentralStockTtypeSku}
              fromPage={fromPage}
              productType={productType}
              siteCode={siteCode}
              warehouseStockDetailUpdate={warehouseStockDetailUpdate}
            ></WarehouseEMEAGridData>
          </RootBox>
        )}
      </Modal>
    </>
  );
}

const RootBox = styled(Box)(({ theme }: { theme: any }) => ({
  background: theme.palette.background.paper,
  borderRadius: 5,
  boxShadow: '24px solid #000',
  display: 'flex',
  flexDirection: 'column',
  left: '50%',
  outline: 'none',
  padding: theme.spacing(3),
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'auto',
}));
